import styled, { css } from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import { Flex } from '@radix-ui/themes';

import { ColorInput } from './ColorInput.jsx';
import DeprecatedText from '../../../../shared/Text';
import { Banner as WelcomeBanner } from '../../../../shared/WelcomeBanner/BannerContainer.jsx';
import { useBannerColor } from '../../../../shared/WelcomeBanner/useBannerColor';
import { useBannerForm } from './useBannerForm';
import { UrlInput } from './UrlInput';

export const Banner = ({ campaign }) => {
  const isDisabled = campaign.isArchived || !!campaign.deletedAt;
  const { values, handleChange } = useBannerForm({
    id: campaign.uuid,
    name: campaign.name,
    color: campaign.color,
    welcomeMessage: campaign.welcomeMessage,
  });

  const { textColor, boardColor } = useBannerColor({
    primaryColor: values.color,
  });

  return (
    <Container>
      <Flex
        style={{ zIndex: '0', position: 'relative' }}
        direction="column"
        gap="3"
      >
        <WelcomeBanner boardColor={boardColor}>
          <TextAreaGrow
            as="div"
            variant="h3"
            color={textColor}
            data-replicated-value={values.name}
          >
            <TextArea
              as="textarea"
              variant="h3"
              color={textColor}
              value={values.name}
              disabled={isDisabled}
              onChange={e => handleChange('name', e.target.value)}
            />
          </TextAreaGrow>
          <TextAreaGrow
            as="div"
            variant="body3"
            color={textColor}
            data-replicated-value={values.welcomeMessage}
          >
            <TextArea
              as="textarea"
              variant="body3"
              color={textColor}
              disabled={isDisabled}
              value={values.welcomeMessage}
              onChange={e => handleChange('welcomeMessage', e.target.value)}
            />
          </TextAreaGrow>

          {!isDisabled && (
            <ColorInput
              color={campaign.color}
              setColor={color => handleChange('color', color)}
            />
          )}
        </WelcomeBanner>
        <UrlInput campaign={campaign} />
      </Flex>
    </Container>
  );
};

const Container = styled.div`
  width: 100%;

  ${themeGet('mediaQueries.forExtraLargeUp')} {
    width: 1090px;
  }
`;

const textAreaStyles = css`
  background: transparent;
  border: 1px solid ${({ color }) => color};
  border-radius: 8px;
  padding: 4px 8px;
  margin: 0;
  width: 580px;
  word-break: break-word;

  grid-area: 1 / 1 / 2 / 2;
`;

const TextAreaGrow = styled(DeprecatedText)`
  display: grid;
  max-width: 580px;
  position: relative;

  &:first-of-type {
    margin: 0 0 16px;
  }

  &::after {
    content: attr(data-replicated-value) ' ';
    white-space: pre-wrap;
    visibility: hidden;
    ${textAreaStyles}
  }

  &::before {
    border-radius: 8px;
    content: ' ';
    display: block;
    position: absolute;
    left: 0;
    top: 0;
    width: 580px;
    height: 100%;
    opacity: 0.2;
    background-repeat: no-repeat;
    background-position: 50% 0;
    background-size: cover;
    background-color: ${themeGet('colors.white')};
  }
`;

const TextArea = styled(DeprecatedText)`
  resize: none;
  overflow: hidden;
  position: relative;

  ${textAreaStyles}

  &:focus {
    background: transparent;
  }
`;
